import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { FontAwesomeIcon } from '@magoosh/lib/icons';

import { useAITutorContext } from '@test_prep/app/ai_tutor/context';
import { AvailableSkill } from '@test_prep/app/ai_tutor/types';

import GradientPill from '../gradient_pill';
import { CrossIcon } from '../icons';

import Button from './button';
import Home from './home';
import Messages from './messages';

import style from './styles.module.scss';

const Launcher: React.FC<{}> = (props) => {
  const { hideTutor, pushToStack, showTutor, tutorActive, tutoringSessionsStack } = useAITutorContext();

  // These data fetch keys control when the tabs will refresh their data. We check for
  // new data every time the messages tab is shown and just on first launch for the home tab.
  const [homeDataFetchKey, setHomeDataFetchKey] = useState<string>(null);
  const [messagesDataFetchKey, setMessagesDataFetchKey] = useState<string>(null);
  const [selectedTab, setSelectedTab] = useState<'home' | 'messages'>('home');

  const expanded = tutorActive && !tutoringSessionsStack.length;

  useEffect(() => {
    if (expanded) {
      if (selectedTab === 'messages') {
        setMessagesDataFetchKey(uuidv4());
      } else if (selectedTab === 'home' && !homeDataFetchKey) {
        setHomeDataFetchKey(uuidv4());
      }
    }
  }, [expanded]);

  useEffect(() => {
    document.addEventListener('aiTutor.launchHome', showTutor);

    return () => {
      document.removeEventListener('aiTutor.launchHome', showTutor);
    }
  }, []);

  const launchAvailableSkill = (availableSkill: AvailableSkill) => {
    if (availableSkill.domain.name === 'Customer Support') {
      (window as any).Forethought('widget', 'open');
      hideTutor();
    } else {
      pushToStack({ domain: availableSkill.domain, initialSkill: availableSkill.skill });
    }
  };

  const switchTab = (tab: 'home' | 'messages') => {
    if (tab === 'messages') setMessagesDataFetchKey(uuidv4());
    setSelectedTab(tab);
  }

  return (
    <div className={cx(style.launcherContainer, 'align-items-center d-flex justify-content-center')}>
      <div className={tutorActive ? 'hidden' : null}>
        <Button onClick={showTutor} />
      </div>

      <div
        className={cx(
          style.contentContainer,
          { hidden: !expanded }
        )}
      >
        <div className="d-flex flex-1 flex-column" style={{ height: '100%' }}>
          <div
            className="align-items-center d-flex"
            style={{
              borderBottom: '1px solid #F2F2F2',
              boxShadow: '0px 8px 8px 0px rgba(0, 0, 0, 0.02)',
              padding: '12px 20px'
            }}
          >
            <div className="flex-none">
              <GradientPill size="sm">
                <FontAwesomeIcon icon="asterisk"/> Help
              </GradientPill>
            </div>

            <div className="d-flex flex-1 justify-content-end">
              <a
                className="align-items-center d-flex"
                onClick={hideTutor}
                style={{ color: '#9AA0A9', height: '100%', padding: 7 }}
              >
                <CrossIcon size={16} />
              </a>
            </div>
          </div>

          <div
            className="flex-1"
            style={{ overflowY: 'scroll', overscrollBehavior: 'contain', padding: 20 }}
          >
            <div className={cx({ hidden: selectedTab !== 'home' })}>
              <Home
                dataFetchKey={homeDataFetchKey}
                onSelected={launchAvailableSkill}
              />
            </div>

            <div className={cx({ hidden: selectedTab !== 'messages' })}>
              <Messages
                dataFetchKey={messagesDataFetchKey}
                onSessionSelected={(tutoringSession) => pushToStack(tutoringSession)}
                />
            </div>
          </div>

          <div
            className="d-flex"
            style={{
              boxShadow: 'rgba(9, 14, 21, 0.06) 0px 0px 25px',
              borderTop: '1px solid #F5F5F5',
              padding: '15px 0'
            }}
          >
            <div
              className="align-items-center d-flex flex-1 flex-column justify-content-middle"
              onClick={() => switchTab('home')}
              style={{ cursor: selectedTab !== 'home' ? 'pointer' : 'initial' }}
            >
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="27" height="26" fill="none">
                  <path fill={selectedTab === 'home' ? '#4D2079' : '#9AA0A9'} d="M14.855.434a1.622 1.622 0 0 0-2.21 0L1.268 10.998a1.628 1.628 0 0 0-.406 1.782 1.62 1.62 0 0 0 1.508 1.031h.813v8.939A3.253 3.253 0 0 0 6.433 26h14.628a3.253 3.253 0 0 0 3.25-3.25V13.81h.814c.67 0 1.27-.411 1.513-1.03.244-.62.081-1.331-.406-1.783L14.855.434Zm-1.92 15.815h1.625a2.439 2.439 0 0 1 2.438 2.438v4.875h-6.501v-4.875a2.439 2.439 0 0 1 2.438-2.438Z" />
                </svg>
              </div>

              <div
                className="small text-bold"
                style={{ color: selectedTab === 'home' ? '#4D2079' : '#9AA0A9' }}
              >
                Home
              </div>
            </div>

            <div
              className="align-items-center d-flex flex-1 flex-column justify-content-middle"
              onClick={() => switchTab('messages')}
              style={{ cursor: selectedTab !== 'messages' ? 'pointer' : 'initial' }}
            >
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="27" height="26" fill="none">
                  <path fill={selectedTab === 'messages' ? '#4D2079' : '#9AA0A9'} d="M3.689 13.122a2.168 2.168 0 0 0-.253-2.072 5.275 5.275 0 0 1-1.02-3.106c0-3.015 2.73-5.777 6.5-5.777 3.768 0 6.498 2.762 6.498 5.777 0 3.016-2.73 5.778-6.499 5.778a7.196 7.196 0 0 1-2.044-.293 2.152 2.152 0 0 0-1.417.067l-2.275.912.514-1.286H3.69ZM.25 7.944c0 1.616.524 3.12 1.43 4.37L.337 15.672a1.21 1.21 0 0 0 1.575 1.575l4.346-1.737c.84.248 1.733.379 2.658.379 4.784 0 8.665-3.557 8.665-7.945C17.58 3.557 13.7 0 8.915 0 4.131 0 .25 3.557.25 7.944Zm17.33 16.612c.93 0 1.82-.136 2.659-.38l4.346 1.738a1.21 1.21 0 0 0 1.575-1.575l-1.34-3.358c.902-1.255 1.43-2.758 1.43-4.37 0-3.72-2.785-6.838-6.544-7.705a9.66 9.66 0 0 1-.456 2.117c2.884.668 4.838 3.038 4.838 5.593a5.276 5.276 0 0 1-1.02 3.105 2.167 2.167 0 0 0-.253 2.072l.515 1.287-2.275-.912a2.181 2.181 0 0 0-1.417-.068c-.64.19-1.327.293-2.044.293-3.259 0-5.736-2.062-6.35-4.568a11.5 11.5 0 0 1-2.17.235c.74 3.697 4.273 6.5 8.52 6.5l-.014-.004Z" />
                </svg>
              </div>

              <div
                className="small text-bold"
                style={{ color: selectedTab === 'messages' ? '#4D2079' : '#9AA0A9' }}
              >
                Messages
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Launcher;
